import Vue from "vue";
import VueRouter from "vue-router";
import RouteObjects from "@/shared/router/route-objects";
import ParticipantStore from "@/participant/store";

var router = {};

if (window.location.host.match(/my.*\.penchecks\.com/g) !== null) {
  Vue.use(VueRouter);

  const routes = [
    {
      path: "/",
      name: "Home",
      meta: { /* title: "Home", */ allowsAnonymousUser: true },
      component: () => import("../views/PcParticipantHome.vue"),
    },
    {
      path: "/login",
      name: "Login",
      meta: { /* title: "Home", */ allowsAnonymousUser: true },
      component: () => import("../views/PcLogin.vue"),
    },
    {
      path: "/start",
      name: "Get Started",
      meta: { /* title: "Home", */ allowsAnonymousUser: true },
      component: () => import("../views/PcStart.vue"),
    },
    {
      path: "/verify-identity",
      name: "Verify Identity",
      component: () => import("../views/PcPersonaVerification.vue"),
    },
    {
      path: "/confirm-identity",
      name: "Confirm Identity",
      component: () => import("../views/PcConfirmIdentity.vue"),
    },
    {
      path: "/contact-support",
      name: "Contact Support",
      meta: { allowsAnonymousUser: true },
      component: () => import("../views/PcContactSupport.vue"),
    },
    {
      path: "/forgot-password",
      name: "Forgot Password",
      meta: { allowsAnonymousUser: true },
      component: () => import("../views/PcForgotPassword.vue"),
    },
    {
      path: "/forgot-email",
      name: "Forgot Email",
      meta: { allowsAnonymousUser: true },
      component: () => import("../views/PcForgotEmail.vue"),
    },
    {
      path: "/reset-password/:email_address/:reset_password_token",
      name: "Reset Password",
      meta: { allowsAnonymousUser: true },
      component: () => import("../views/PcResetPassword.vue"),
    },
    {
      path: "/confirm-email-address",
      name: "Confirm Email Address",
      meta: { allowsAnonymousUser: true },
      component: () => import("../views/PcConfirmEmailAddress.vue"),
    },
    {
      path: "/change-email-address/:emailAddress/:token",
      name: "Change Email Address",
      meta: { allowsAnonymousUser: true },
      component: () => import("../views/PcChangeEmailAddress.vue"),
    },
    {
      path: "/create-login",
      name: "Create Login",
      component: () => import("../views/PcCreateLogin.vue"),
    },
    {
      path: "/my-distributions",
      name: "My Distributions",
      component: () => import("../views/PcMyDistributions.vue"),
    },
    {
      path: "/name-change-request",
      name: "Name Change Request",
      component: () => import("../views/PcNameChangeRequest.vue"),
    },
    {
      path: "/my-tax-forms",
      name: "My Tax Forms",
      component: () => import("../views/PcMyTaxForms.vue"),
    },
    {
      path: "/my-account",
      name: "My Account",
      component: () => import("../views/PcMyAccount.vue"),
    },
    {
      path: "/my-account/create-login",
      name: "My Account - Create Login",
      component: () => import("../views/PcMyAccount.vue"),
    },
    {
      path: "/my-account/edit-login",
      name: "My Account - Edit Login",
      component: () => import("../views/PcMyAccount.vue"),
    },
    {
      path: "/my-account/edit-login/change-email-address",
      name: "My Account - Edit login - Change email address",
      component: () => import("../views/PcMyAccount.vue"),
    },
    {
      path: "/my-account/edit-login/change-password",
      name: "My Account - Edit login - Change password",
      component: () => import("../views/PcMyAccount.vue"),
    },
    {
      path: "/my-account/edit-my-information",
      name: "My Account - Edit my information",
      component: () => import("../views/PcMyAccount.vue"),
    },
    {
      path: "/my-account/edit-contact",
      name: "My Account - Edit contact",
      component: () => import("../views/PcMyAccount.vue"),
    },
    {
      path: "/distributions/:orderLineUuid([a-zA-Z0-9]{32})/setup",
      name: "Setup Distribution(s)",
      component: () => import("../views/PcDistributionSetup.vue"),
    },
    {
      path: "/distributions/:orderLineUuid([a-zA-Z0-9]{32})/details",
      name: "View Distribution",
      component: () => import("../views/PcDistributionDetails.vue"),
    },
    {
      path: "/duplicate-account",
      name: "Duplicate Account",
      component: () => import("../views/PcDuplicateAccount.vue"),
      meta: { title: "Duplicate Account" },
    },
  ];

  router = new VueRouter({
    mode: "history",
    routes,
    scrollBehavior(to, from, savedPosition) {
      if (savedPosition) {
        return savedPosition;
      } else {
        return { x: 0, y: 0 };
      }
    },
  });

  router.objects = new RouteObjects(router);

  // Onboarding routes that are protected from backward navigation.
  // Back-button blocking is handled by PcBackButtonBanner (history.pushState barrier).
  // This guard handles manual URL entry and programmatic navigation to wrong pages.
  const ONBOARDING_ROUTES = [
    "/start",
    "/verify-identity",
    "/confirm-identity",
    "/contact-support",
    "/duplicate-account",
  ];

  router.beforeEach((to, from, next) => {
    const resolveRoute = () => {
      const user = ParticipantStore.state.authenticatedUser;
      const allowsAnonymous = to.matched.some(
        (r) => r.meta.allowsAnonymousUser
      );

      // During onboarding, only allow access to the current step's page
      // and the next step's page. All other routes redirect to current step.
      if (user?.isOnboardingCompleted === false) {
        const onboardingRoutes = {
          emailConfirmationStep: "/start",
          passwordSetupStep: "/start",
          informIdentityCheckStep: "/start",
          identityVerificationStep: "/verify-identity",
          contactUsStep: "/contact-support",
          piiConfirmationStep: "/confirm-identity",
          duplicateAccountStep: "/duplicate-account",
        };

        const nextOnboardingRoutes = {
          informIdentityCheckStep: "/verify-identity",
          identityVerificationStep: "/confirm-identity",
        };

        const targetRoute =
          onboardingRoutes[user.currentOnboardingStep] || "/start";

        // Allow navigation to contact-support when Persona extraction failed
        // (prevents redirect loop back to confirm-identity)
        if (
          to.path === "/contact-support" &&
          (to.query.reason === "extraction-failed" ||
            to.query.reason === "pii-incorrect")
        ) {
          return next();
        }

        const nextRoute = nextOnboardingRoutes[user.currentOnboardingStep];
        const allowedRoutes = [targetRoute, nextRoute].filter(Boolean);

        if (!allowedRoutes.includes(to.path)) {
          return next({ path: targetRoute, replace: true });
        }

        return next();
      }

      // Completed onboarding — block navigation back to onboarding pages
      if (user?.isOnboardingCompleted && ONBOARDING_ROUTES.includes(to.path)) {
        return next({ path: "/my-distributions", replace: true });
      }

      // Anonymous routes
      if (allowsAnonymous) {
        if (user && to.path === "/start") {
          return next("/my-distributions");
        }

        const canAccessProtected =
          user?.isOnboardingCompleted &&
          !user?.isIdVerificationRequired &&
          user?.lastPasswordChangeAt !== null;

        const shouldRedirect =
          canAccessProtected &&
          !["/my-distributions", "/confirm-email-address"].includes(to.path);

        return next(shouldRedirect ? "/my-distributions" : undefined);
      }

      // Require authentication
      if (!user) {
        return next(to.path === "/" ? undefined : "/");
      }

      // Prevent IDV access when not needed
      const isIdvRoute = ["/verify-identity", "/confirm-identity"].includes(
        to.path
      );
      const idvNotRequired =
        !user.isIdVerificationRequired || user.isOnboardingCompleted;

      if (isIdvRoute && idvNotRequired) {
        return next("/my-distributions");
      }

      next();
    };

    // Wait for auth to load
    if (ParticipantStore.state.isLoadingAuthenticatedUser) {
      const unwatch = ParticipantStore.watch(
        (state) => state.isLoadingAuthenticatedUser,
        (loading) => {
          if (!loading) {
            unwatch();
            resolveRoute();
          }
        }
      );
    } else {
      resolveRoute();
    }
  });

  router.afterEach((to) => {
    if (to.meta && to.meta.title) {
      document.title = to.meta.title + " | PenChecks Participant";
    } else {
      document.title = "PenChecks Participant";
    }

    if (router.app && router.app.$gtag) {
      router.app.$gtag.pageview({
        page_title: document.title,
        page_path: to.path,
        page_location: window.location.href,
      });
    }
  });
}

export default router;
